<template>
  <div class="unified-chat" :class="chatType">
     <!-- Chat Header -->
     <div class="chat-header">
       <h3 class="chat-title">{{ headerTitle }}</h3>
       <div class="chat-meta">
         <span v-if="agentStatus" class="agent-status">{{ agentStatus }}</span>

         <!-- Chat Mode Selector -->
         <select
           :value="chatMode"
           class="mode-selector"
           @input="onModeChange"
         >
           <option value="model">Direct Model</option>
           <option value="agent">Agent Chat</option>
         </select>

         <!-- Model Selector (only for model mode) -->
         <select
           v-if="chatMode === 'model' && showModelSelector && availableModels.length > 0"
           v-model="selectedModel"
           class="model-selector"
           @change="onModelChange"
         >
           <option value="">Select Model</option>
           <option
             v-for="model in availableModels"
             :key="model.id"
             :value="model.id"
           >
             {{ model.name }}
           </option>
         </select>
         <span v-else-if="chatMode === 'model' && modelName" class="model-badge">{{ modelName }}</span>

         <button v-if="showSettings" class="btn-settings" @click="$emit('settings')">
           ⚙️
         </button>
       </div>
     </div>

    <!-- Welcome Screen -->
    <div v-if="!hasMessages" class="chat-welcome">
      <div class="welcome-icon">{{ welcomeIcon }}</div>
      <h4 class="welcome-title">{{ welcomeTitle }}</h4>
      <p class="welcome-description">{{ welcomeDescription }}</p>

      <!-- Quick Prompts -->
      <div v-if="contextualPrompts.length > 0" class="quick-prompts">
        <button
          v-for="prompt in contextualPrompts"
          :key="prompt.id"
          @click="$emit('quick-prompt', prompt)"
          class="quick-prompt"
        >
          {{ prompt.label }}
        </button>
      </div>
    </div>

    <!-- Messages Area -->
    <div class="chat-messages" ref="messagesRef">
      <!-- Existing Messages -->
      <div
        v-for="(message, index) in safeMessages"
        :key="index"
        :class="['message', message.role]"
      >
        <div class="message-content" v-html="renderContent(message.content)"></div>

        <!-- Suggested Tasks (Goal Chat) -->
        <div v-if="message.suggested_tasks && message.suggested_tasks.length > 0" class="suggested-tasks">
          <div class="suggested-header">
            <span>Suggested Tasks ({{ message.suggested_tasks.length }})</span>
            <button @click="$emit('apply-all-tasks', message.suggested_tasks)" class="btn-apply-all">
              Create All
            </button>
          </div>
          <div
            v-for="(task, taskIndex) in message.suggested_tasks"
            :key="'task-' + index + '-' + taskIndex"
            class="suggested-task-item"
          >
            <div class="task-info">
              <span class="task-title">{{ task.title }}</span>
              <span v-if="task.suggested_type" class="task-type-badge">{{ formatTaskType(task.suggested_type) }}</span>
            </div>
            <button @click="$emit('apply-single-task', task)" class="btn-apply-one">+</button>
          </div>
        </div>

        <div class="message-meta">
          <span class="role">{{ message.role }}</span>
          <span v-if="message.timestamp" class="time">{{ formatTime(message.timestamp) }}</span>
        </div>
      </div>

      <!-- Typing Indicator -->
      <div v-if="isTyping" class="message assistant thinking">
        <div class="thinking-dots">
          <span></span><span></span><span></span>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="chat-input-section">
      <textarea
        v-model="input"
        :placeholder="inputPlaceholder"
        @keydown.enter.exact.prevent="sendMessage"
        :disabled="disabled"
        rows="2"
        class="chat-input"
      ></textarea>
      <button
        @click="sendMessage"
        :disabled="disabled || !input.trim()"
        class="send-button"
      >
        <span v-if="isLoading" class="spinner-xs"></span>
        <span v-else>→</span>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, watch, onMounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'UnifiedChat',
  props: {
    chatType: {
      type: String,
      required: true,
      validator: (value) => ['workstream', 'goal', 'task'].includes(value)
    },
    targetId: { type: String, default: '' },
    agentId: { type: String, default: '' },
    modelId: { type: String, default: '' },
    chatMode: { type: String, default: 'model', validator: (value) => ['model', 'agent'].includes(value) },
    width: { type: Number, default: 400 },
    messages: { type: Array, default: () => [] },
    isTyping: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    showModelSelector: { type: Boolean, default: true },
    availableModels: { type: Array, default: () => [] }
  },
  emits: ['send-message', 'quick-prompt', 'apply-all-tasks', 'apply-single-task', 'settings', 'model-change', 'mode-change', 'update:chatMode'],
  setup(props, { emit }) {
    const store = useStore()
    const input = ref('')
    const messagesRef = ref(null)
    const selectedModel = ref(props.modelId)
    const chatMode = ref(props.chatMode)

    // Watch for prop changes and update local state
    watch(() => props.chatMode, (newMode) => {
      chatMode.value = newMode
    })

    // Computed properties based on chat type
    const headerTitle = computed(() => {
      switch (props.chatType) {
        case 'workstream': return 'Workstream Assistant'
        case 'goal': return 'Task Planning Assistant'
        case 'task': return 'Task Assistant'
        default: return 'Assistant'
      }
    })

    const welcomeIcon = computed(() => {
      switch (props.chatType) {
        case 'workstream': return '🤖'
        case 'goal': return '🎯'
        case 'task': return '⚡'
        default: return '💬'
      }
    })

    const welcomeTitle = computed(() => {
      switch (props.chatType) {
        case 'workstream': return 'Analyze your workstream'
        case 'goal': return 'Plan your tasks'
        case 'task': return 'Complete your task'
        default: return 'Get help'
      }
    })

    const welcomeDescription = computed(() => {
      switch (props.chatType) {
        case 'workstream': return 'Get insights, analyze progress, and identify next priorities for your workstream.'
        case 'goal': return 'Break down this goal into actionable tasks and get planning guidance.'
        case 'task': return 'Get help completing this task with step-by-step guidance.'
        default: return 'Ask me anything to help you move forward.'
      }
    })

    const inputPlaceholder = computed(() => {
      switch (props.chatType) {
        case 'workstream': return 'Ask about workstream progress, priorities, or analysis...'
        case 'goal': return 'Ask about task planning, prioritization, or milestones...'
        case 'task': return 'Ask for help with this task, guidance, or next steps...'
        default: return 'Type your message...'
      }
    })

    const contextualPrompts = computed(() => {
      switch (props.chatType) {
        case 'workstream':
          return [
            { id: 'status', label: 'Workstream status summary' },
            { id: 'bottlenecks', label: 'Identify bottlenecks' },
            { id: 'priorities', label: 'Next priority tasks' },
            { id: 'analysis', label: 'Team productivity analysis' },
            { id: 'risks', label: 'Risk assessment' }
          ]
        case 'goal':
          return [
            { id: 'breakdown', label: 'Break down into tasks' },
            { id: 'prioritize', label: 'Prioritize tasks' },
            { id: 'milestones', label: 'Key milestones' },
            { id: 'resources', label: 'Resource requirements' },
            { id: 'timeline', label: 'Timeline estimation' }
          ]
        case 'task':
          return [
            { id: 'start', label: 'Help me start this task' },
            { id: 'steps', label: 'Break into steps' },
            { id: 'focus', label: 'What should I focus on?' },
            { id: 'progress', label: 'Update progress' },
            { id: 'help', label: 'Ask team for help' }
          ]
        default:
          return []
      }
    })

    const availableModels = computed(() => {
      // First check if models were passed as a prop
      if (props.availableModels && Array.isArray(props.availableModels) && props.availableModels.length > 0) {
        return props.availableModels
      }
      // Fall back to store
      const rawModels = store.state.llm?.items || []
      return Array.isArray(rawModels) ? rawModels.filter(model => model && typeof model === 'object' && model.id) : []
    })

    const agentStatus = computed(() => {
      // For workstream and task chats, show model status instead of agent status
      if (props.chatType === 'workstream' || props.chatType === 'task') {
        if (selectedModel.value) {
          const model = availableModels.value.find(m => m.id === selectedModel.value)
          return model ? `Using ${model.name}` : null
        }
        return 'Select Model'
      }
      return null
    })

    const modelName = computed(() => {
      if (selectedModel.value && availableModels.value.length > 0) {
        const model = availableModels.value.find(m => m.id === selectedModel.value)
        return model ? model.name : null
      }
      return null
    })

    const showSettings = computed(() => {
      return props.chatType === 'workstream'
    })

    const hasMessages = computed(() => {
      return props.messages && Array.isArray(props.messages) && props.messages.length > 0
    })

    const safeMessages = computed(() => {
      return Array.isArray(props.messages) ? props.messages : []
    })

    // Methods
    const sendMessage = () => {
      if (!input.value.trim() || props.disabled) return

      const message = input.value.trim()
      input.value = ''
      emit('send-message', message, selectedModel.value, chatMode.value)
    }

    const onModelChange = () => {
      emit('model-change', selectedModel.value)
    }

    const onModeChange = (event) => {
      const newMode = event.target.value
      chatMode.value = newMode
      emit('mode-change', newMode)
      emit('update:chatMode', newMode)
    }

    const renderContent = (content) => {
      if (!content) return ''
      if (typeof content !== 'string') return String(content)

      let escaped = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      escaped = escaped.replace(/`([^`]+)`/g, '<code>$1</code>')

      escaped = escaped.replace(/```([\s\S]*?)```/g, '<pre>$1</pre>')

      escaped = escaped.replace(
        /(https?:\/\/[^\s)]+)(?=[).,!?]*\s|$)/g,
        m => `<a href="${m}" target="_blank" rel="noopener noreferrer">${m}</a>`
      )

      return escaped
    }

    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    const formatTaskType = (type) => {
      // TODO: Implement task type formatting
      return type || 'Task'
    }

    // Auto-scroll to bottom when new messages arrive
    watch(() => safeMessages.value, () => {
      nextTick(() => {
        // Add small delay to ensure DOM has fully updated
        setTimeout(() => {
          scrollToBottom()
        }, 50)
      })
    }, { deep: true })

    onMounted(() => {
      // Ensure component is properly initialized
      nextTick(() => {
        setTimeout(() => {
          scrollToBottom()
        }, 50)
      })
    })

    // Safe scroll function with null checks
    const scrollToBottom = () => {
      if (messagesRef.value && messagesRef.value.scrollHeight !== undefined) {
        try {
          const element = messagesRef.value
          // Ensure the element has proper dimensions
          if (element.clientHeight > 0 && element.scrollHeight > element.clientHeight) {
            element.scrollTop = element.scrollHeight - element.clientHeight
          }
        } catch (error) {
          // Silently handle scroll errors (e.g., during hot reloading)
          console.debug('Scroll error (likely during hot reload):', error)
        }
      }
    }

    return {
      input,
      messagesRef,
      selectedModel,
      headerTitle,
      welcomeIcon,
      welcomeTitle,
      welcomeDescription,
      inputPlaceholder,
      contextualPrompts,
      availableModels,
      agentStatus,
      modelName,
      showSettings,
      hasMessages,
      safeMessages,
      sendMessage,
      onModelChange,
      renderContent,
      formatTime,
      formatTaskType
    }
  }
}
</script>

<style scoped>
.unified-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.chat-header {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
}

.chat-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.chat-meta {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.agent-status {
  background: #e8f5e8;
  color: #2e7d32;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.model-selector {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  color: #495057;
  min-width: 120px;
}

.model-selector:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.mode-selector {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  color: #495057;
  min-width: 100px;
  margin-right: 0.5rem;
}

.mode-selector:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.model-badge {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.btn-settings {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.btn-settings:hover {
  background: #e9ecef;
  color: #495057;
}

.chat-welcome {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: #6c757d;
}

.welcome-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.welcome-title {
  margin: 0 0 0.5rem;
  font-size: 1.2rem;
  color: #2c3e50;
}

.welcome-description {
  margin: 0 0 2rem;
  line-height: 1.5;
  max-width: 300px;
}

.quick-prompts {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 280px;
}

.quick-prompt {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #495057;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.quick-prompt:hover {
  background: #e9ecef;
  border-color: #007bff;
  color: #007bff;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}

.message {
  padding: 1rem;
  border-radius: 8px;
  max-width: 85%;
}

.message.user {
  align-self: flex-end;
  background: #007bff;
  color: white;
}

.message.assistant {
  align-self: flex-start;
  background: #f8f9fa;
  color: #2c3e50;
  border: 1px solid #e9ecef;
}

.message-content {
  margin-bottom: 0.5rem;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  max-width: 100%;
  overflow-x: auto;
}

.message-content a {
  color: #007bff;
  text-decoration: underline;
}

.message-content pre {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  overflow-x: auto;
  max-width: 100%;
  margin: 0.5rem 0;
  border: 1px solid #e9ecef;
}

.message-content code {
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  white-space: pre-wrap;
  word-wrap: break-word;
  border: 1px solid #e9ecef;
}

.message-content pre code {
  background: none;
  padding: 0;
  border: none;
  font-size: 0.9em;
}

.suggested-tasks {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.suggested-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.btn-apply-all {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-apply-all:hover {
  background: #218838;
}

.suggested-task-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  border: 1px solid #e9ecef;
}

.task-info {
  flex: 1;
}

.task-title {
  font-weight: 500;
  color: #2c3e50;
}

.task-type-badge {
  background: #e9ecef;
  color: #495057;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.btn-apply-one {
  background: #007bff;
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  transition: background-color 0.2s;
}

.btn-apply-one:hover {
  background: #0056b3;
}

.thinking {
  display: flex;
  align-items: center;
}

.thinking-dots {
  display: flex;
  gap: 4px;
}

.thinking-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6c757d;
  animation: thinking 1.4s infinite ease-in-out;
}

.thinking-dots span:nth-child(2) { animation-delay: 0.2s; }
.thinking-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes thinking {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}

.message-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  opacity: 0.7;
  margin-top: 0.5rem;
}

.role {
  font-weight: 500;
  text-transform: capitalize;
}

.time {
  font-size: 0.75rem;
}

.chat-input-section {
  padding: 1rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  background: #f8f9fa;
}

.chat-input {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  resize: none;
  font-family: inherit;
  transition: border-color 0.2s;
}

.chat-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.chat-input:disabled {
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.send-button {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 8px;
  background: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 1.2rem;
}

.send-button:hover:not(:disabled) {
  background: #0056b3;
}

.send-button:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.spinner-xs {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Chat type specific styles */
.unified-chat.workstream {
  /* 350px width handled by parent */
}

.unified-chat.goal,
.unified-chat.task {
  /* 400px width handled by parent */
}

/* Responsive design */
@media (max-width: 768px) {
  .quick-prompts {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  .quick-prompt {
    flex: 1;
    min-width: 200px;
  }

  .message {
    max-width: 90%;
  }
}
</style>